/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - European Union 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [media-gallery.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 27.05.18 14:16
 */
import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import Lightbox from 'react-image-lightbox';
import {Tooltip} from "../../components/navigation/buttons/nav-buttons";
import {Popover} from "../../components/navigation/popover/popover";
import {EditIcon, FavoriteIcon, FavoriteIconFilled, fs_regular} from "../../components/navigation/icons/nav-icons";
import {useLanguage} from "../../components/configuration/configuration";
import 'react-image-lightbox/style.css';

export const LikeToolbarItem = ({likes, isLiked, onLike}) => {

    const renderLikeIcon = () => {
        return isLiked ? <FavoriteIconFilled className='clr-red'/> :
            <FavoriteIcon className='clr-white'/>
    }
    return <div className='mr-2'>
        <div className='svg-icon-btn' onClick={onLike}>
            <div className='d-flex justify-content-lg-end align-items-lg-center'>
                {likes > 0 && <span className='small mr-1'>{likes}</span>}
                <span>{renderLikeIcon()}</span>
            </div>
        </div>
    </div>
}

export const EditToolbarItem = ({handleGalleryPopover}) => {
    const Lang = useLanguage();
    return <Tooltip className='mr-3'>
        <Popover className='popover-status-container mr-3'
                 placement='bottom'
                 render={handleGalleryPopover}>
            <div className='svg-icon-btn'>
                <EditIcon size={fs_regular} className="clr-white mr-1"/>
                <span className='small hide-mobile-text'>{Lang.gallery.page.edit}</span>
            </div>
        </Popover>
    </Tooltip>
}

/**
 * LighgtBoxGallery
 * example for a caption: ['Hey wo ist das Bild gemacht worden? Cool.', <p><Send/> <br/>nanan</p>,'abc', 'Hello again']
 * currently media can be an array of LightBoxEntry objects or simply an array of plain urls
 * const LightboxEntry = objectOf('mediaId, url, title, description');
 *
 *  -- refactor to use everywhere just objects for media --
 */
const LightboxGallery = forwardRef(({media,
                             index = 0,
                             open = false,
                             onClose = null,
                             onEdit = null,
                             onLike = null,
                             onChange = null,
                             ...otherProps}, ref) => {
    const [currentIndex, setCurrentIndex] = useState(index);
    const [isOpen, setIsOpen] = useState(open);
    const [imageData, setImageData] = useState({
        imageTitle: onEdit ? media[currentIndex].title : null,
        imageCaption: onEdit ? media[currentIndex].description : null});
    const[likeData, setLikeData] = useState({
        mediaId: onLike ? media[currentIndex].mediaId : null,
        likes: onLike ? media[currentIndex].likes : 0,
        isLiked: onLike ? media[currentIndex].isLiked : false,
    })

    useEffect(() => {
        setImageData({
            imageTitle: onEdit ? media[currentIndex].title : null,
            imageCaption: onEdit ? media[currentIndex].description : null});
    }, [])

    // parent calls method after click on save in a form (for ex. popover component)
    // and after a database update
    useImperativeHandle(ref, () => ({
        onImageDataChange(imageData) {
            setImageData(imageData);
        },
        onLikeDataChange(likeData) {
            setLikeData(likeData);
        },
    }))

    // simulate click and wait for response from server before updating this data
    const handleOnLike = () => {
        onLike && onLike({...likeData, isLiked: !likeData.isLiked});
    }

    const handleOnClose = () => {
        setIsOpen(false);
        onClose && onClose();
    }

    const handleOnPrev = () => {
        const previous = (currentIndex + media.length - 1) % media.length;
        setCurrentIndex(previous)
        onChange && onChange(previous);
        setImageData({imageTitle: media[previous].title, imageCaption: media[previous].description});
        setLikeData({...likeData, isLiked:  media[previous].isLiked, likes: media[previous].likes});
    }

    const handleOnNext = () => {
        const next = (currentIndex + 1) % media.length;
        setCurrentIndex(next);
        onChange && onChange(next);
        setImageData({imageTitle: media[next].title, imageCaption: media[next].description});
        setLikeData({...likeData, isLiked:  media[next].isLiked, likes: media[next].likes});

    }

    // TODO  -- dropin: yet-another-react-lightbox
    const renderNavigationButtons = () => {
        if (media.length > 1) {
            return <div
                style={{
                    position: 'fixed',
                    zIndex: 99999,      /* Forces buttons to sit on top of everything */
                    bottom: '30px',     /* Positions them at the bottom of the screen */
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '15px'
                }}
            >
                <button
                    onClick={() => setCurrentIndex((currentIndex + media.length - 1) % media.length)}
                    style={{ padding: '12px 24px', cursor: 'pointer', background: '#fff', color: '#000', border: 'none', borderRadius: '4px' }}
                >
                    ◀ Previous
                </button>

                <button
                    onClick={() => setCurrentIndex((currentIndex + 1) % media.length)}
                    style={{ padding: '12px 24px', cursor: 'pointer', background: '#fff', color: '#000', border: 'none', borderRadius: '4px' }}
                >
                    Next ▶
                </button>
            </div>
        }
        return null;
    }

    const getOtherProps = () => {
        const toolbarButtons = [];
        otherProps = {...otherProps, mainSrc: media[currentIndex].url}

        if(onEdit) {
            otherProps = {...otherProps, imageTitle: imageData.imageTitle}
            otherProps = {...otherProps, imageCaption:  imageData.imageCaption}

            toolbarButtons.push(<EditToolbarItem handleGalleryPopover={onEdit}/>);
        }
        if(onLike) {
            toolbarButtons.push(<LikeToolbarItem likes={likeData.likes}
                                                 isLiked={likeData.isLiked}
                                                 onLike={handleOnLike}/>);
        }
        if(toolbarButtons.length > 0) {
            otherProps = {...otherProps, toolbarButtons: toolbarButtons}
        }
        return otherProps;
    }

    if (!isOpen) return null;

    return <div>
        <Lightbox
            // nextSrc={media.length > 1 ? media[(currentIndex + 1) % media.length] : null}
            // prevSrc={media.length > 1 ? media[(currentIndex + media.length - 1) % media.length] : null}
            onCloseRequest={handleOnClose}
            onMovePrevRequest={handleOnPrev}
            onMoveNextRequest={handleOnNext}
            {...getOtherProps()}
        />
        {renderNavigationButtons()}
    </div>
})

export default LightboxGallery;